import type { SupabaseClient } from "@supabase/supabase-js";

import type { PolygonFeatureCollection } from "./map-points";
import { pointInPolygon, ringSignedArea } from "../lib/point-in-polygon";
import { ringFromCoordinates, ringToPolygonGeoJson, type Ring } from "./site-boundary";

// PRD-001Y Y3 — asset / building zones (layer L1). Many GeoJSON Polygons per site (buildings,
// parking, gates, ...), stored server-side as jsonb (no PostGIS) and rendered as labeled zones
// between the L0 site boundary and the checkpoint layers. Reads come from
// public.management_site_assets (management-scoped); writes go through the schedule.manage-gated
// save_site_asset / clear_site_asset RPCs. Pure mappers + GeoJSON builders keep the editor and
// map on a unit-tested core, and the GeoJSON parsing helpers are shared with the Y2 L0 module.

export const ASSET_TYPES = ["building", "parking", "gate", "other"] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

export const ASSET_TYPE_LABEL: Record<AssetType, string> = {
  building: "Building",
  parking: "Parking",
  gate: "Gate",
  other: "Other",
};

export function asAssetType(value: unknown): AssetType {
  return typeof value === "string" && (ASSET_TYPES as readonly string[]).includes(value)
    ? (value as AssetType)
    : "building";
}

export interface SiteAsset {
  siteAssetId: string;
  siteId: string;
  siteName: string | null;
  name: string;
  assetType: AssetType;
  source: string;
  ring: Ring;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

/** Map a management_site_assets row → SiteAsset, or null when the polygon is unusable. */
export function mapSiteAssetRow(raw: Record<string, unknown>): SiteAsset | null {
  const geojson = raw.boundary_geojson;
  if (geojson === null || typeof geojson !== "object") {
    return null;
  }
  const geo = geojson as Record<string, unknown>;
  if (geo.type !== "Polygon") {
    return null;
  }
  const ring = ringFromCoordinates(geo.coordinates);
  if (ring === null) {
    return null;
  }
  return {
    siteAssetId: asString(raw.site_asset_id) ?? "",
    siteId: asString(raw.site_id) ?? "",
    siteName: asString(raw.site_name),
    name: asString(raw.name) ?? "Zone",
    assetType: asAssetType(raw.asset_type),
    source: asString(raw.source) ?? "drawn",
    ring,
  };
}

/** GeoJSON FeatureCollection of asset zone polygons for the CAD map L1 layer. */
export function toSiteAssetFeatureCollection(assets: readonly SiteAsset[]): PolygonFeatureCollection {
  return {
    type: "FeatureCollection",
    features: assets.map((asset) => ({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [asset.ring] },
      properties: {
        id: asset.siteAssetId,
        site: asset.siteName ?? "",
        name: asset.name,
        type: asset.assetType,
      },
    })),
  };
}

const ASSET_LIMIT = 1000;

export async function fetchSiteAssets(client: SupabaseClient, accountId: string): Promise<SiteAsset[]> {
  const { data, error } = await client
    .from("management_site_assets")
    .select("site_asset_id, account_id, site_id, site_name, name, asset_type, boundary_geojson, source, updated_at")
    .eq("account_id", accountId)
    .limit(ASSET_LIMIT);
  if (error !== null) {
    throw new Error("Could not load site assets.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .map((row) => mapSiteAssetRow(row as Record<string, unknown>))
    .filter((asset): asset is SiteAsset => asset !== null);
}

/** Return the smallest active asset zone containing a map point (client-side v1). */
export function findContainingSiteAsset(
  assets: readonly SiteAsset[],
  lat: number,
  lng: number,
): SiteAsset | null {
  let best: SiteAsset | null = null;
  let bestArea = Number.POSITIVE_INFINITY;
  for (const asset of assets) {
    if (!pointInPolygon(lng, lat, asset.ring)) {
      continue;
    }
    const area = Math.abs(ringSignedArea(asset.ring));
    if (area < bestArea) {
      best = asset;
      bestArea = area;
    }
  }
  return best;
}

export type AssetWriteOutcome = { ok: true } | { ok: false; reason: string };

export const SAFE_ASSET_WRITE_FAILURE = "Couldn't save that asset zone — please try again.";

// Contract-stable, value-free sentences from the asset RPCs; anything else fails closed.
const KNOWN_ASSET_FAILURES: ReadonlySet<string> = new Set([
  "Account id is required to save a site asset.",
  "Site id is required to save a site asset.",
  "Caller is not authorized to manage schedules for this account.",
  "Site was not found for this account.",
  "Site asset name is required.",
  "Site asset type is not supported.",
  "Site asset source is not supported.",
  "Site asset must be a GeoJSON Polygon with a closed ring of at least four positions.",
]);

function assetReason(error: { message?: unknown } | null): string {
  const message = typeof error?.message === "string" ? error.message : "";
  return KNOWN_ASSET_FAILURES.has(message) ? message : SAFE_ASSET_WRITE_FAILURE;
}

export async function saveSiteAsset(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
  ring: Ring,
  options: { name: string; assetType?: AssetType; source?: string },
): Promise<AssetWriteOutcome> {
  try {
    const { error } = await client.rpc("save_site_asset", {
      p_account_id: accountId,
      p_site_id: siteId,
      p_name: options.name,
      p_asset_type: options.assetType ?? "building",
      p_boundary_geojson: ringToPolygonGeoJson(ring),
      p_source: options.source ?? "drawn",
    });
    return error === null ? { ok: true } : { ok: false, reason: assetReason(error) };
  } catch {
    return { ok: false, reason: SAFE_ASSET_WRITE_FAILURE };
  }
}

export async function clearSiteAsset(
  client: SupabaseClient,
  accountId: string,
  siteAssetId: string,
): Promise<AssetWriteOutcome> {
  try {
    const { error } = await client.rpc("clear_site_asset", {
      p_account_id: accountId,
      p_site_asset_id: siteAssetId,
    });
    return error === null ? { ok: true } : { ok: false, reason: assetReason(error) };
  } catch {
    return { ok: false, reason: SAFE_ASSET_WRITE_FAILURE };
  }
}
